/**
 * Relocation Profile Panel (TelePortal Setup)
 *
 * Displays the same profile data as the Settings → Profile Mobility card
 * (address first, then citizenship, passport, household, work model,
 * countries of interest), with a checkbox per field for the user to confirm.
 * Continue is always allowed; unchecked fields are still passed to the next widget.
 *
 * Data source: user profile (useUserProfile) mapped to onboarding shape.
 * Guide mode: receives WidgetProps (config, onComplete).
 */

import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';
import type { WidgetProps } from '@tonnie/copilot';
import { useCountryName, useTranslation } from '@tonnie/locale';
import { useUserProfile } from '@tonnie/sdk';
import { Badge, Button, Card, Checkbox, CountryFlag, cn } from '@tonnie/ui';
import { Globe, MapPin } from '@tonnie/assets';
import { ArrowRight, Bookmark, ChevronLeft, Users } from '@tonnie/assets';
import { type IOnboardingData, mapTalentToProfileReview } from '@tonnie/profile-review';

/** @deprecated Widget now uses profile data; kept for CoPilotHybrid type compatibility. */
export interface Finding {
  id: string;
  label: string;
  value: unknown;
}

// ── Map profile to onboarding data ──
// Uses the shared mapper from `@tonnie/profile-review` so the
// customFields → IOnboardingData contract stays in lockstep with
// the Settings profile sections and business-web TalentDetail.

function mapProfileToOnboardingData(
  profile: NonNullable<ReturnType<typeof useUserProfile>['data']>
): IOnboardingData {
  return mapTalentToProfileReview(profile.talent);
}

// ── Field ids for confirmation checkboxes ──

const FIELD_IDS = [
  'address',
  'citizenship',
  'passport',
  'household',
  'workModel',
  // L4 · F45 · D-27 — countries of interest are no longer asked, confirmed or shown.
] as const;

type FieldId = (typeof FIELD_IDS)[number];

// ── Components ──

interface RelocationProfilePanelProps extends WidgetProps {
  progress?: number;
  isPinned?: boolean;
  onTogglePin?: () => void;
  onCollapse?: () => void;
  className?: string;
}

export function RelocationProfilePanel({
  progress = 0,
  isPinned,
  onTogglePin,
  onCollapse,
  className,
  config,
  onComplete,
}: RelocationProfilePanelProps) {
  const { t } = useTranslation();
  const countryName = useCountryName();
  const { data: profile } = useUserProfile();

  const [confirmed, setConfirmed] = useState<Record<FieldId, boolean>>(() =>
    FIELD_IDS.reduce((acc, id) => ({ ...acc, [id]: false }), {} as Record<FieldId, boolean>)
  );

  const profileData = profile ? mapProfileToOnboardingData(profile) : null;
  const mobility = profileData?.mobility;
  // The server's one list (`talent.citizenships`), canonical first.
  const citizenships = profileData?.citizenships ?? [];
  const notProvided = t('profile.notProvided');

  const hasAny =
    profileData &&
    (profileData.address?.formattedAddress ||
      citizenships.length > 0 ||
      mobility?.hasValidPassport !== undefined ||
      profileData.familyConfig != null ||
      (profileData.workModelPreference?.length ?? 0) > 0);

  const setConfirmedField = useCallback((id: FieldId, value: boolean) => {
    setConfirmed((prev) => ({ ...prev, [id]: value }));
  }, []);

  const handleComplete = useCallback(() => {
    onComplete?.({
      profileData: profileData ?? undefined,
      confirmed: { ...confirmed },
    });
  }, [onComplete, profileData, confirmed]);

  const title = (config?.title as string) || t('copilot.findings.profileTitle');
  const description = config?.description as string | undefined;

  return (
    <div
      className={cn(
        'flex flex-col h-full min-h-0 rounded-lg border border-border bg-card',
        className
      )}
      data-testid="widget-relocation_profile"
    >
      {/* Header */}
      <div className="shrink-0 px-4 py-3 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-primary-accessible" />
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          </div>
          <div className="flex items-center gap-1">
            {onTogglePin && (
              <button
                type="button"
                onClick={onTogglePin}
                className={cn(
                  'p-1 rounded hover:bg-muted/50 transition-colors',
                  isPinned ? 'text-primary-accessible' : 'text-muted-foreground'
                )}
                aria-label={
                  isPinned
                    ? t('teleportal.setup.aria.unpinPanel')
                    : t('teleportal.setup.aria.pinPanel')
                }
              >
                <Bookmark className="w-3.5 h-3.5" />
              </button>
            )}
            {onCollapse && (
              <button
                type="button"
                onClick={onCollapse}
                className="p-1 rounded hover:bg-muted/50 transition-colors text-muted-foreground"
                aria-label={t('teleportal.setup.aria.collapsePanel')}
              >
                <ChevronLeft className="w-3.5 h-3.5 rotate-180" />
              </button>
            )}
          </div>
        </div>
        {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
        {progress > 0 && (
          <div className="mt-2">
            <div className="h-1 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Body: one Mobility-style card with Address first + 5 mobility rows, each with checkbox */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
        {!hasAny ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <div className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center mb-2">
              <Users className="w-5 h-5 text-muted-foreground/50" />
            </div>
            <p className="text-sm text-muted-foreground">{t('copilot.findings.emptyProfile')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('copilot.findings.profileIntro')}
            </p>

            <Card className="rounded-lg bg-card text-card-foreground shadow-sm border-2 hover:border-primary/50 hover:shadow-lg transition-all duration-300">
              <div className="flex flex-col space-y-1.5 p-6 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Globe className="w-5 h-5 text-primary-accessible" />
                    </div>
                    <div>
                      <h3 className="tracking-tight text-lg font-semibold">
                        {t('profile.mobility')}
                      </h3>
                      <p className="text-muted-foreground text-xs mt-0.5">
                        {t('profile.mobilityDescription')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-6 pt-0 space-y-5">
                {/* 1. Address (first) – label + checkbox on one row, value in muted box below */}
                <div
                  className="space-y-2 pt-4 border-t border-border/50"
                  data-testid="widget-relocation_profile-field-address"
                >
                  <div className="flex justify-between items-center gap-4 text-sm">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                      <MapPin className="w-3 h-3" />
                      {t('profile.address')}
                    </p>
                    <Checkbox
                      checked={confirmed.address}
                      onCheckedChange={(val) => setConfirmedField('address', val === true)}
                      aria-label={t('profile.address')}
                    />
                  </div>
                  {profileData?.address?.formattedAddress ? (
                    <div className="bg-muted/30 rounded-lg p-3 space-y-1.5">
                      <p className="text-sm font-medium text-foreground">
                        {profileData.address.formattedAddress}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm font-medium text-muted-foreground">{notProvided}</p>
                  )}
                </div>

                {/* 2–6. Same rows as Settings Mobility card */}
                <div className="space-y-2">
                  <ConfirmableRow
                    label={t('onboarding.mobility.citizenship')}
                    checked={confirmed.citizenship}
                    onCheckedChange={(val) => setConfirmedField('citizenship', val === true)}
                  >
                    {citizenships.length > 0 ? (
                      <div
                        className="flex flex-wrap gap-1 justify-end"
                        data-testid="widget-relocation_profile-citizenships"
                      >
                        {citizenships.map((code) => (
                          <Badge key={code} variant="secondary" className="text-xs">
                            <CountryFlag code={code} size="sm" className="mr-1.5" />
                            {countryName(code)}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm font-medium">{notProvided}</span>
                    )}
                  </ConfirmableRow>

                  <ConfirmableRow
                    label={t('onboarding.mobility.validPassport')}
                    checked={confirmed.passport}
                    onCheckedChange={(val) => setConfirmedField('passport', val === true)}
                  >
                    <span className="text-sm font-medium">
                      {mobility?.hasValidPassport === true
                        ? t('common.yes')
                        : mobility?.hasValidPassport === false
                          ? t('common.no')
                          : notProvided}
                    </span>
                  </ConfirmableRow>

                  <ConfirmableRow
                    label={t('onboarding.family.householdInfo')}
                    checked={confirmed.household}
                    onCheckedChange={(val) => setConfirmedField('household', val === true)}
                  >
                    <span className="text-sm font-medium">
                      {(() => {
                        const parts: string[] = [];
                        if (profileData?.familyConfig?.hasPartner)
                          parts.push(t('onboarding.family.hasPartner'));
                        if (profileData?.familyConfig?.hasChildren)
                          parts.push(
                            `${t('onboarding.family.hasChildren')} (${profileData.familyConfig.childrenCount ?? 0})`
                          );
                        if (profileData?.familyConfig?.hasPets)
                          parts.push(t('onboarding.family.hasPets'));
                        return parts.length > 0 ? parts.join(', ') : notProvided;
                      })()}
                    </span>
                  </ConfirmableRow>

                  <ConfirmableRow
                    label={t('onboarding.personalInfo.workModelPreference')}
                    checked={confirmed.workModel}
                    onCheckedChange={(val) => setConfirmedField('workModel', val === true)}
                  >
                    {profileData?.workModelPreference &&
                    profileData.workModelPreference.length > 0 ? (
                      <div className="flex flex-wrap gap-1 justify-end">
                        {profileData.workModelPreference.map((m) => (
                          <Badge key={m} variant="outline" className="text-xs">
                            {m}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm font-medium">{notProvided}</span>
                    )}
                  </ConfirmableRow>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>

      {onComplete && (
        <div className="shrink-0 px-4 py-3 border-t border-border">
          <Button
            className="w-full"
            onClick={handleComplete}
            data-testid="widget-relocation_profile-edit"
          >
            {t('copilot.findings.looksGood')}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
}

interface ConfirmableRowProps {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  children: ReactNode;
}

function ConfirmableRow({ label, checked, onCheckedChange, children }: ConfirmableRowProps) {
  return (
    <div className="flex justify-between items-start gap-4 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <div className="flex items-center gap-2 min-w-0 flex-1 justify-end">
        <div className="text-right font-medium min-w-0">{children}</div>
        <Checkbox
          checked={checked}
          onCheckedChange={(val) => onCheckedChange(val === true)}
          aria-label={label}
        />
      </div>
    </div>
  );
}
